import React, { useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "@ai360/core/intl";

import { Loader } from "~/core";
import { ImportWizardHeader } from "./import-wizard-header/import-wizard-header";
import { ImportWizardPanel } from "./import-wizard-panel/import-wizard-panel";
import { ImportWizardFooter } from "./import-wizard-footer/import-wizard-footer";
import { FieldResurfacePrompt } from "../../../../common";

import { ImportTypes, PanelTypes } from "../actions";
import { messages } from "../i18n-messages";

import "./import-wizard.css";
import { ImportFileInfo, ImportTemplate } from "~/file-import/model";
import {
    IImportFieldList,
    FieldStripRecord,
} from "~/action-panel/components/import-module/components/import-wizard/models";
import { EventAPI, FileImportAPI } from "@ai360/core";

interface IStep {
    id: number;
    label: string;
    panelType: string;
    options: any;
}

const _getStepList = (formatMessage, importType, isSingleStep, isUpdate): IStep[] => {
    switch (importType.name.toLowerCase()) {
        case ImportTypes.FIELD_BOUNDARY: {
            return isSingleStep
                ? [
                      {
                          id: 0,
                          label: formatMessage(
                              isUpdate
                                  ? messages.fieldBoundaryUpdateText
                                  : messages.fieldBoundaryImportText
                          ),
                          panelType: PanelTypes.FIELD_BOUNDARY_PANEL,
                          options: {},
                      },
                  ]
                : [
                      {
                          id: 0,
                          label: formatMessage(messages.selectionText),
                          panelType: PanelTypes.FIELD_BOUNDARY_COLUMNS,
                          options: {},
                      },
                      {
                          id: 1,
                          label: formatMessage(
                              isUpdate ? messages.updateText : messages.importText
                          ),
                          panelType: PanelTypes.FIELD_BOUNDARY_PANEL,
                          options: {},
                      },
                  ];
        }
        case ImportTypes.SAMPLING_SOIL:
        case ImportTypes.SAMPLING_NEMATODE:
        case ImportTypes.SAMPLING_TISSUE: {
            return isSingleStep
                ? [
                      {
                          id: 0,
                          label: formatMessage(
                              messages.importWizardCreateEventsSamplingCentroidText
                          ),
                          panelType: PanelTypes.EVENTS_PANEL,
                          options: {
                              ignoreSelectedField: true,
                              isFilteringVisible: true,
                              isSingleStep: true,
                          },
                      },
                  ]
                : [
                      {
                          id: 0,
                          label: formatMessage(messages.importWizardFilesText),
                          panelType: PanelTypes.FILES_PANEL,
                          options: {
                              ignoreSelectedField: true,
                              isFilteringVisible: false,
                          },
                      },
                      {
                          id: 1,
                          label: formatMessage(messages.importWizardFieldsText),
                          panelType: PanelTypes.FIELDS_PANEL,
                          options: {
                              ignoreSelectedField: false,
                              isFilteringVisible: true,
                          },
                      },
                      {
                          id: 2,
                          label: formatMessage(messages.importWizardEventsText),
                          panelType: PanelTypes.EVENTS_PANEL,
                          options: {
                              ignoreSelectedField: true,
                              isFilteringVisible: true,
                          },
                      },
                  ];
        }
        case ImportTypes.MANAGEMENT_AREA: {
            return [
                {
                    id: 0,
                    label: formatMessage(messages.importWizardFilesText),
                    panelType: PanelTypes.FILES_PANEL,
                    options: {
                        ignoreSelectedField: true,
                        isFilteringVisible: false,
                    },
                },
                {
                    id: 1,
                    label: formatMessage(messages.importWizardFieldsText),
                    panelType: PanelTypes.FIELDS_PANEL,
                    options: {
                        ignoreSelectedField: false,
                        isFilteringVisible: true,
                    },
                },
                {
                    id: 2,
                    label: formatMessage(messages.importWizardLayersLabelText),
                    panelType: PanelTypes.MANAGEMENT_AREA_PANEL,
                    options: {
                        ignoreSelectedField: true,
                        isFilteringVisible: true,
                    },
                },
            ];
        }
        default: {
            return [
                {
                    id: 0,
                    label: formatMessage(messages.importWizardFilesText),
                    panelType: PanelTypes.FILES_PANEL,
                    options: {
                        ignoreSelectedField: true,
                        isFilteringVisible: false,
                    },
                },
                {
                    id: 1,
                    label: formatMessage(messages.importWizardFieldsText),
                    panelType: PanelTypes.FIELDS_PANEL,
                    options: {
                        ignoreSelectedField: false,
                        isFilteringVisible: true,
                    },
                },
                {
                    id: 2,
                    label:
                        importType.name.toLowerCase() !== ImportTypes.IMAGERY
                            ? formatMessage(messages.importWizardEventsText)
                            : formatMessage(messages.importWizardLayersText, {
                                  count: 2,
                              }),
                    panelType: PanelTypes.EVENTS_PANEL,
                    options: {
                        ignoreSelectedField: true,
                        isFilteringVisible: true,
                    },
                },
            ];
        }
    }
};
const _getSubmitLabel = (
    formatMessage,
    importType,
    importFieldList,
    eventStripCount = 0,
    isResultsTemplate = false
): string => {
    switch (importType.name.toLowerCase()) {
        case ImportTypes.FIELD_BOUNDARY: {
            const isUpdate = importFieldList.length > 0 ? importFieldList[0].isUpdate : false;
            const message = isUpdate
                ? messages.importWizardUpdateFieldText
                : messages.importWizardAddFieldText;
            return formatMessage(message, { count: importFieldList.length });
        }
        case ImportTypes.IMAGERY:
            return formatMessage(messages.importWizardCreateLayersText, {
                count: eventStripCount,
            });
        case ImportTypes.MANAGEMENT_AREA:
            return formatMessage(messages.importWizardCreateLayersText, {
                count: eventStripCount,
            });
        default:
            return formatMessage(
                isResultsTemplate
                    ? messages.importWizardImportText
                    : messages.importWizardCreateEventsText,
                { count: eventStripCount }
            );
    }
};
const _getActiveStep = (stepId, stepList: IStep[]) => {
    return stepList.find((step) => {
        return step.id === stepId;
    });
};

export interface IImportWizardProps {
    changeStep: (index: number) => void;
    closeImportWizard: (isCancel: boolean) => void;
    currentStep: number;
    eventStripCount: number | null;
    fieldsToResurface: string[];
    hasSubmitted: boolean;
    ignoreSelectedField: boolean;
    isFilteringVisible: boolean;
    isFieldMatchingComplete: boolean;
    isFieldMatchingLoading: boolean;
    isLoading: boolean;
    importTemplates: Map<string, ImportTemplate> | null;
    isValidating: boolean;
    isResultsTemplate: boolean;
    importFieldStripData: FieldStripRecord[];
    importFieldList: IImportFieldList[];
    importFileGuidList: string[] | null;
    importFiles: Map<string, ImportFileInfo> | null;
    importType: FileImportAPI.IImportType;
    setIgnoreSelectedField: (value: boolean) => void;
    setIsFilteringVisible: (value: boolean) => void;
    setShowImportPoints: (visible: boolean) => void;
    showImportPoints: boolean;
    setFieldBoundarySubmitLoading: (value: boolean) => void;
    submitFieldBoundary: () => void;
    validateImportEvent: () => void;
    loadDetailPage: () => void;
    onFetchResurfaceEvents: (fieldGuids: string[], keepExistingEvents: boolean) => void;
    onSetFieldEditsChanged: () => void;
    onSetEventsToResurface: (eventsToResurface: string[]) => void;
    resurfaceEvents: EventAPI.IResurfaceEvent[];
}

export const ImportWizard_ = (props: IImportWizardProps): JSX.Element => {
    const {
        changeStep,
        closeImportWizard,
        currentStep,
        eventStripCount,
        fieldsToResurface,
        hasSubmitted,
        ignoreSelectedField,
        isFilteringVisible,
        isFieldMatchingComplete,
        isFieldMatchingLoading,
        isLoading,
        importTemplates,
        isValidating,
        isResultsTemplate,
        importFieldStripData,
        importFieldList,
        importFileGuidList,
        importFiles,
        importType,
        setIgnoreSelectedField,
        setIsFilteringVisible,
        setShowImportPoints,
        showImportPoints,
        setFieldBoundarySubmitLoading,
        submitFieldBoundary,
        validateImportEvent,
        loadDetailPage,
        onFetchResurfaceEvents,
        onSetFieldEditsChanged,
        onSetEventsToResurface,
        resurfaceEvents,
    } = props;

    const { formatMessage } = useIntl();

    const isUpdate = useMemo<boolean>(
        () => (importFieldList?.length > 0 ? importFieldList[0].isUpdate : false),
        [importFieldList]
    );

    const isSingleStep = useMemo<boolean>(() => {
        const applicableImportTypes = [
            ImportTypes.SAMPLING_SOIL,
            ImportTypes.SAMPLING_TISSUE,
            ImportTypes.SAMPLING_NEMATODE,
            ImportTypes.FIELD_BOUNDARY,
        ];
        if (!applicableImportTypes.includes(importType.name.toLowerCase())) {
            return false;
        }

        const isFieldBoundary = importType.name.toLowerCase() === ImportTypes.FIELD_BOUNDARY;
        let foundFile = false;

        for (const guid of importFileGuidList) {
            const targetFile = importFiles.get(guid);
            if (!targetFile) {
                continue;
            }
            foundFile = true;
            const targetTemplate = importTemplates.get(targetFile.templateGuid);
            if (!targetTemplate) {
                continue;
            }
            if (
                targetTemplate.isNameMatch ||
                (isFieldBoundary && !targetTemplate.name.startsWith("Column Selection"))
            ) {
                //Some templates only require one import wizard step
                return true;
            }
        }
        return isFieldBoundary && !foundFile;
    }, [importType, importFileGuidList, importFiles, importTemplates]);

    const [canSubmit, setCanSubmit] = useState(false);
    const [canContinue, setCanContinue] = useState(false);
    const [importAnalysisLayers, setImportAnalysisLayers] = useState(false);
    const [showResurfacePrompt, setShowResurfacePrompt] = useState(false);
    const stepList = useMemo<IStep[]>(
        () => _getStepList(formatMessage, importType, isSingleStep, isUpdate),
        [formatMessage, importType, isSingleStep, isUpdate]
    );

    const updateFieldBoundary = () => {
        if (fieldsToResurface && fieldsToResurface.length > 0) {
            setFieldBoundarySubmitLoading(true);
            onSetFieldEditsChanged();
            onFetchResurfaceEvents(fieldsToResurface, true);
            if (!showResurfacePrompt && resurfaceEvents) {
                setShowResurfacePrompt(true);
            }
        } else {
            submitFieldBoundary();
        }
    };

    const onSubmitClick = () => {
        if (canSubmit) {
            setCanSubmit(false);
            switch (importType.name.toLowerCase()) {
                case ImportTypes.FIELD_BOUNDARY:
                    updateFieldBoundary();
                    break;
                case ImportTypes.MANAGEMENT_AREA:
                    setImportAnalysisLayers(true);
                    break;
                default:
                    validateImportEvent();
            }
        }
    };

    const _onResurfacePromptClosed = (resurfaceEventResult) => {
        onSetEventsToResurface(resurfaceEventResult);
        setFieldBoundarySubmitLoading(false);
    };

    const _onResurfacePromptCanceled = () => {
        setFieldBoundarySubmitLoading(false);
        setShowResurfacePrompt(false);
    };

    // Track previous props to replicate UNSAFE_componentWillReceiveProps behavior.
    const prevCurrentStepRef = useRef(currentStep);
    const prevResurfaceLengthRef = useRef(resurfaceEvents?.length);
    const prevResurfaceEventsRef = useRef(resurfaceEvents);

    // Handle current step changes.
    useEffect(() => {
        if (currentStep !== prevCurrentStepRef.current) {
            setImportAnalysisLayers(false);
            setCanContinue(false);
            const activeStep = _getActiveStep(currentStep, stepList);

            if (activeStep?.options) {
                if (activeStep.options.ignoreSelectedField !== ignoreSelectedField) {
                    setIgnoreSelectedField(activeStep.options.ignoreSelectedField);
                }
                if (activeStep.options.isFilteringVisible !== isFilteringVisible) {
                    setIsFilteringVisible(activeStep.options.isFilteringVisible);
                }
            }
        }
        prevCurrentStepRef.current = currentStep;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep]);

    // Handle resurface events changes for field boundary imports.
    useEffect(() => {
        const isFieldBoundary = importType.name.toLowerCase() === ImportTypes.FIELD_BOUNDARY;
        const lengthChanged = resurfaceEvents?.length !== prevResurfaceLengthRef.current;

        if (isFieldBoundary && lengthChanged) {
            setShowResurfacePrompt(resurfaceEvents?.length > 0);
            if (resurfaceEvents?.length === 0) {
                submitFieldBoundary();
            }
        }

        if (
            isFieldBoundary &&
            resurfaceEvents !== prevResurfaceEventsRef.current &&
            resurfaceEvents === null
        ) {
            submitFieldBoundary();
        }

        prevResurfaceLengthRef.current = resurfaceEvents?.length;
        prevResurfaceEventsRef.current = resurfaceEvents;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resurfaceEvents, importType]);

    const submitLabel = _getSubmitLabel(
        formatMessage,
        importType,
        importFieldList,
        eventStripCount,
        isResultsTemplate
    );
    const activeStep = _getActiveStep(currentStep, stepList);
    const fieldPanelActive = activeStep ? activeStep.panelType === PanelTypes.FIELDS_PANEL : true;
    const fieldResurfaceProps = {
        resurfaceEvents: resurfaceEvents,
        isOpen: showResurfacePrompt,
        onClose: (resurfaceEventDetails) => _onResurfacePromptClosed(resurfaceEventDetails),
        onCancel: _onResurfacePromptCanceled,
        isImportUpdate: true,
    };

    return (
        <React.Fragment>
            <div className="import-wizard">
                {isLoading ||
                (isFieldMatchingLoading && (fieldPanelActive || isSingleStep)) ||
                isValidating ? (
                    <Loader />
                ) : null}
                <ImportWizardHeader
                    currentStep={currentStep}
                    importFieldStripData={importFieldStripData}
                    importType={importType.name.toLowerCase()}
                    stepList={stepList}
                    setShowImportPoints={setShowImportPoints}
                    showImportPoints={showImportPoints}
                    isFieldMatchingComplete={isFieldMatchingComplete}
                />
                <ImportWizardPanel
                    canContinue={canContinue}
                    canSubmit={canSubmit}
                    importAnalysisLayers={importAnalysisLayers}
                    panelType={activeStep?.panelType}
                    updateCanContinue={setCanContinue}
                    updateCanSubmit={setCanSubmit}
                    loadDetailPage={loadDetailPage}
                    importType={importType}
                />
                <ImportWizardFooter
                    canContinue={canContinue}
                    canSubmit={canSubmit}
                    currentStep={currentStep}
                    hasSubmitted={hasSubmitted}
                    importType={importType.name.toLowerCase()}
                    stepList={stepList}
                    submitLabel={submitLabel}
                    onBackClick={() => changeStep(currentStep - 1)}
                    onNextClick={() => changeStep(currentStep + 1)}
                    onSubmitClick={() => onSubmitClick()}
                    onCloseClick={closeImportWizard}
                />
            </div>
            {showResurfacePrompt && <FieldResurfacePrompt {...fieldResurfaceProps} />}
        </React.Fragment>
    );
};
